import React, { useState, useEffect, useRef } from 'react';
import { trackAnalyticsEvent } from "../utils/ui.utils";
import { CARBON_DATA } from "../utils/impact.utils";

/**
 * CalculatorHeader Subcomponent
 */
const CalculatorHeader = ({ onClose }) => (
	<>
		<button className="close-button" onClick={onClose}>×</button>
	</>
);

/**
 * ProductDescription Subcomponent
 */
const ProductDescription = ({ weight, t }) => {
	const getProduct = (w) => {
		if (w >= 1200) return t("calculator.products.winter_comforter");
		if (w >= 700)  return t("calculator.products.heavy_sleeping_bag");
		if (w >= 500)  return t("calculator.products.light_sleeping_bag");
		if (w >= 240)  return t("calculator.products.heavy_parka");
		if (w >= 140)  return t("calculator.products.mid_jacket");
		if (w >= 60)   return t("calculator.products.light_jacket_heavy_vest");
		if (w >= 30)   return t("calculator.products.light_vest");
		return "";
	};

	return (
		<p className="product-type" style={{ textAlign: 'center', fontWeight: 'bold', minHeight: '1.5em' }}>
			{getProduct(weight)}
		</p>
	);
};

/**
 * WeightSlider Subcomponent
 */
const WeightSlider = ({ weight, onWeightChange, t }) => {
	return (
		<div className="range-container">
			<div className="containerTitle" style={{ textAlign: 'center', fontSize: '1.2em' }}>
				<p className="title-text">{t("calculator.selected_weight")}</p><p className="title-value">{weight}g</p>
			</div>
			<ProductDescription weight={weight} t={t} />
			<label htmlFor="weight-slider" className="sr-only" style={{ display: 'none' }}>{t("calculator.select_down_weight")}</label>
			<div className="slider-wrapper" style={{ position: 'relative', width: '100%', margin: '0 auto' }}>
				{/* Top Labels */}
				<div className="top-guides" style={{ position: 'absolute', top: '-23px', left: 0, right: 0, height: '65px', pointerEvents: 'none' }}>
					{[30, 1600].map(pt => {
						const isActive = weight === pt;
						return (
							<div key={pt} className={isActive ? "active" : ""} style={{ position: 'absolute', left: pt === 30 ? '0' : '100%', width: '0', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
								<div style={{ fontSize: '1em', whiteSpace: 'nowrap', color: '#fff', fontWeight: 'bold', marginBottom: '2px' }}>{pt}g</div>
								<div style={{ width: '1px', height: '30px', backgroundColor: '#ccc' }}></div>
							</div>
						);
					})}
				</div>
				<input
					type="range"
					id="weight-slider"
					name="weight"
					min="30"
					max="1600"
					step="1"
					value={weight}
					onChange={onWeightChange}
					list="weight-notches"
					style={{ width: '100%', cursor: 'pointer', position: 'relative', zIndex: 2, margin: 0 }}
				/>

				<datalist id="weight-notches">
					{[30, 60, 140, 240, 500, 700, 1200, 1600].map(pt => (
						<option key={pt} value={pt}>{pt}g</option>
					))}
				</datalist>

				{/* Bottom Labels */}
				<div className="stepGuides" style={{ position: 'relative', width: '100%', padding: '0 8.5px', boxSizing: 'border-box', pointerEvents: 'none', height: '0', top: '-10px' }}>
					{[60, 140, 240, 500, 700, 1200].map((pt, index) => {
						const percent = ((pt - 30) / (1600 - 30)) * 100;
						const adjustedLeft = `calc(${percent}% + (${(0.5 - percent / 100) * 17}px))`;
						const isActive = weight === pt;
						return (
							<div key={pt} className={isActive ? "active" : ""} style={{ position: 'absolute', top: '0', left: adjustedLeft, transform: 'translateX(-50%)', width: '0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
								<div className="tick-line" style={{ width: '1px', height: index % 2 === 0 ? '10px' : '30px', backgroundColor: '#ccc' }}></div>
								<div className="notch-text" style={{ whiteSpace: 'nowrap', textAlign: 'center', marginTop: '2px', width: 'max-content' }}>{pt}g</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

/**
 * Main CarbonCalculator Component
 */
const ENABLE_TOOLTIP_REFACTOR = true;

const CarbonCalculator = React.forwardRef(({ impactScore, activeLot, onClose, t }, ref) => {
	const [weight, setWeight] = useState(140);
	const [showTooltip, setShowTooltip] = useState(false);
	const calculationTimerRef = useRef(null);
	const latestWeightRef = useRef(140);

	const alliedTotal = impactScore * weight;
	const syntheticTotal = CARBON_DATA.SYNTHETIC_BASELINE * weight;

	const calculateImpactReduction = (alliedValue, synthValue) => {
		if (!synthValue || synthValue === 0) return "N/A";
		const reduction = (1 - (alliedValue / synthValue)) * 100;
		const whole = Math.floor(reduction);
		//return reduction.toFixed(1) + "%";
		if (reduction > whole) {return whole + "⁺%";}
	};

	const synthPercent = calculateImpactReduction(alliedTotal, syntheticTotal);

	const flushAnalytics = () => {
		if (calculationTimerRef.current) {
			clearTimeout(calculationTimerRef.current);
			calculationTimerRef.current = null;
			trackAnalyticsEvent({ event: 'calculation', value: `${latestWeightRef.current}g`, lot: activeLot });
		}
	};

	useEffect(() => {
		const dialog = ref.current;
		if (!dialog) return;
		const handleCloseInternal = () => {
			flushAnalytics();
			setWeight(140);
			latestWeightRef.current = 140;
		};
		dialog.addEventListener('close', handleCloseInternal);
		return () => {
			dialog.removeEventListener('close', handleCloseInternal);
			flushAnalytics();
		};
	}, [ref, activeLot]);

	const handleWeightChange = (e) => {
		let newWeight = Number(e.target.value);
		const thresholds = [30, 60, 140, 240, 500, 700, 1200, 1600];
		const nearest = thresholds.find(t => Math.abs(t - newWeight) <= 15);
		if (nearest !== undefined) newWeight = nearest;
		setWeight(newWeight);
		latestWeightRef.current = newWeight;
		if (calculationTimerRef.current) clearTimeout(calculationTimerRef.current);
		calculationTimerRef.current = setTimeout(flushAnalytics, 800);
	};

	return (
		<dialog ref={ref} className="impact-dialog">
			<div className="dialog-content">
				<p className="content_lotID">LOT NO.{activeLot}</p>
				<CalculatorHeader onClose={onClose} />
				<h3>{t("calculator.calculated_impact")}</h3>
				<div className="content_ws"><WeightSlider weight={weight} onWeightChange={handleWeightChange} t={t} /></div>
				<div className="content_table">
					<table style={{ borderCollapse: 'collapse'}}>
						<thead>
							<tr>
								<th style={{ textAlign: 'left', textTransform: 'uppercase' }}>{t("calculator.allied_verified_impact")}</th>
								<th style={{ textAlign: 'left', textTransform: 'uppercase'}}>{t("calculator.synthetic_alternative_estimate")}</th>
							</tr>
						</thead>
						<tbody>
							{ENABLE_TOOLTIP_REFACTOR ? (
								<tr className="spacer"><td style={{ height: '30px'}}></td><td style={{ height: '30px'}}></td></tr>
							) : (
								<tr className="spacer"><td style={{ height: '30px'}}></td><td></td><td style={{ height: '30px'}}></td></tr>
							)}
							<tr>
								<td className="impactScore">
									<span>{alliedTotal.toFixed(5)}</span>
									<small>{t("calculator.kg_co2_total_load")}</small>
								</td>
								<td className="impactScore">
									<p>
										{synthPercent}
										{ENABLE_TOOLTIP_REFACTOR ? (
											<span className="tooltip-trigger" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
												<a href="https://alliedfeather.com/info-room/environmental-impact/carbon-calculator" target="_blank" rel="noopener noreferrer" className="asterisk-link"><sup>*</sup></a>
												<span className={`tooltip-content ${showTooltip ? 'is-visible' : ''}`}>{t("impact.calculator_tooltip")}</span>
											</span>
										) : <sup>*</sup>}
									</p>
									<small>{t("impact.lower_impact")} {t("impact.than_synthetic")}</small>
								</td>
							</tr>
							{ENABLE_TOOLTIP_REFACTOR ? (
								<tr><td style={{ height: '30px'}}></td><td style={{ height: '30px'}}></td></tr>
							) : (
								<tr><td style={{ height: '30px'}}></td><td></td><td style={{ height: '30px'}}></td></tr>
							)}
							<tr>
								<td className="content_table-title">{t("calculator.real_world_equivalent")}</td>
								<td className="content_table-title">{t("calculator.real_world_equivalent")}</td>
							</tr>
							<tr>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value"><strong>{(alliedTotal * 17).toFixed(1)} </strong></td><td className="equivalent-label">{t("calculator.days_tree_sequester")}</td></tr></tbody></table></td>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value synthetic"><strong>{(syntheticTotal * 17).toFixed(1)} </strong></td><td className="equivalent-label">{t("calculator.days_tree_sequester")}</td></tr></tbody></table></td>
							</tr>
							<tr>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value"><strong>{(alliedTotal / 0.265).toFixed(1)} </strong></td><td className="equivalent-label">{t("calculator.km_gasoline_truck")}</td></tr></tbody></table></td>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value synthetic"><strong>{(syntheticTotal / 0.265).toFixed(1)}</strong></td><td className="equivalent-label">{t("calculator.km_gasoline_truck")}</td></tr></tbody></table></td>
							</tr>
							<tr>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value"><strong>{(alliedTotal / 0.03785).toFixed(1)}</strong></td><td className="equivalent-label">{t("calculator.kg_plastic_disposed")}</td></tr></tbody></table></td>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value synthetic"><strong>{(syntheticTotal / 0.03785).toFixed(1)}</strong></td><td className="equivalent-label">{t("calculator.kg_plastic_disposed")}</td></tr></tbody></table></td>
							</tr>
							<tr>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value"><strong>{(alliedTotal / 0.00818).toFixed(1)}</strong></td><td className="equivalent-label">{t("calculator.phone_charges")}</td></tr></tbody></table></td>
								<td><table className="subTable"><tbody><tr><td className="equivalent-value synthetic"><strong>{(syntheticTotal / 0.00818).toFixed(1)}</strong></td><td className="equivalent-label">{t("calculator.phone_charges")}</td></tr></tbody></table></td>
							</tr>
						</tbody>
					</table>
					<footer>
					  <small>
						{t("impact.calculator_footnote")}
						&nbsp;
						{t("impact.more_info_prefix")}
						<a href="https://alliedfeather.com/info-room/environmental-impact/carbon-calculator" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>
							{t("impact.click_here")}
						</a>.
					  </small>
					</footer>				
				</div>
			</div>
		</dialog>
	);
});

export default CarbonCalculator;